import Collection from 'ol/Collection';
import Feature from 'ol/Feature';
import {Vector as SourceVector} from 'ol/source';
import {computed, onUnmounted, ref, Ref, shallowRef, triggerRef, watch} from 'vue';
import KlDrawZoneMapUtils, {EDrawZoneMapFeaturePropertyKey} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map-utils';
import BaseEvent from 'ol/events/Event';

export interface IDrawZoneFeatureContext {
    projectionRef: Ref<string>;
    setProjection: (projection: string) => void;

    currentAreaRef: Ref<number>;
    updateCurrentArea: (area: number) => void;

    currentFeatureRef: Ref<Feature>;
    source: SourceVector;
    selectedFeatures: Collection<Feature>;

    addFeature: (feature: Feature) => void;
    removeFeature: (feature: Feature) => void;
    findFeature: (zoneId: number) => Feature;
    setCurrentFeature: (feature: Feature) => void;
    getCurrentFeature: () => Feature;
    selectFeature: (feature: Feature) => void;
    backupFeature: (zoneId: number) => void;
    revertFeatureChanges: () => void;
    clear: () => void ;
}

export function useDrawZoneFeatureContext(): IDrawZoneFeatureContext {

    const _currentFeature: Ref<Feature> = shallowRef<Feature>(null);

    // used for 'watch'
    const currentFeatureRef = computed(() => _currentFeature.value);

    let _featureBackup: Feature = null;
    const _mapFeatures: Collection<Feature> = new Collection();

    const source = new SourceVector({ features: _mapFeatures });
    const selectedFeatures: Collection<Feature> = new Collection();

    const _projection: Ref<string> = ref<string>(null);
    const projectionRef = computed(() => _projection.value)
    const setProjection = (projection: string) => {
        _projection.value = projection;
    }

    const _currentArea: Ref<number> = ref<number>(null);
    const currentAreaRef = computed(() => _currentArea.value)
    const updateCurrentArea = (area: number) => {
        _currentArea.value = area;
    }

    const addFeature = (feature: Feature) => {
        _mapFeatures.push(feature);
    }

    const removeFeature = (feature: Feature) => {
        _mapFeatures.remove(feature);
        selectedFeatures.remove(feature);

        if (feature === _currentFeature.value) {
            setCurrentFeature(null);
        }
    }

    const findFeature = (zoneId: number): Feature => {
        let feature: Feature = null;
        _mapFeatures.forEach((f: Feature) => {
            if (f.get(EDrawZoneMapFeaturePropertyKey.drawZoneId) === zoneId) {
                feature = f;
            }
        });
        return feature;
    }

    const _onCurrentFeatureChange = (event: BaseEvent) => {
        triggerRef(_currentFeature);
    }

    const setCurrentFeature = (feature: Feature) => {
        if (_currentFeature.value) {
            _currentFeature.value.un('change', _onCurrentFeatureChange);
        }

        _currentFeature.value = feature;

        if (_currentFeature.value) {
            _currentFeature.value.on('change', _onCurrentFeatureChange);
        }
    }
    const getCurrentFeature = (): Feature => {
        return _currentFeature.value;
    }

    const selectFeature = (feature: Feature) => {
        selectedFeatures.clear();
        if (feature) {
            selectedFeatures.push(feature);
        }
    }

    const backupFeature = (zoneId: number) => {
        const feature = findFeature(zoneId);
        if (feature) {
            _featureBackup = feature.clone();
        }
    }

    const revertFeatureChanges = () => {
        if ((selectedFeatures.getLength() === 0) || !_featureBackup) {
            return;
        }
        selectedFeatures.item(0).setGeometry(_featureBackup.getGeometry());
    }

    const clear = () => {
        setCurrentFeature(null);

        _featureBackup = null;

        _mapFeatures.clear();
        selectedFeatures.clear();
    }

    onUnmounted(() => {
        setCurrentFeature(null);
    })

    watch (_currentFeature, (newFeature: Feature) => {
        _currentArea.value = KlDrawZoneMapUtils.getArea(newFeature);
    });

    return {
        projectionRef,
        setProjection,

        currentAreaRef,
        updateCurrentArea,

        currentFeatureRef,
        source,
        selectedFeatures,

        addFeature,
        removeFeature,
        findFeature,
        setCurrentFeature,
        getCurrentFeature,
        selectFeature,
        backupFeature,
        revertFeatureChanges,
        clear,
    }
}
